import { type Dispatch, type FC, type ReactNode, useLayoutEffect, useRef, useState } from "react";

import { augmentDataPathWithModelTypes, isTriggerNode, type NodeModelWithId } from "@doitintl/cloudflow-commons";
import { ModelType, type ReferencedNodeValue } from "@doitintl/cmp-models";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Chip, type Palette, type SxProps, Tooltip } from "@mui/material";

import { cmpBaseColors } from "../../../../../../../cmpBaseColors";
import { colors } from "../../../../../../../muiTheme";
import { ThemeModes } from "../../../../../../../muiThemeTypes";
import {
  ReferencedFieldChipCustomToken,
  type ReferencedFieldChipCustomTokenProps,
} from "./ReferencedFieldChipCustomToken";
import { type NodeWitOutputModel } from "./useReferencedFieldContext";
import { useResizeObserverSignal } from "./useResizeObserverSignal";
import { useVariableReference } from "./useVariableReference";
import { processTriggerNodeTokens } from "./utils/fieldTokenUtils";

export const ellipsisSx: SxProps = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
};

function getReferencedNodeLabel(referencedNode: NodeWitOutputModel) {
  return `${referencedNode.stepNumber}. ${referencedNode.name}`;
}

function getBorderColor(palette: Palette, selected: boolean) {
  if (selected) {
    return palette.primary.border;
  }
  return palette.mode === ThemeModes.DARK ? palette.primary.light : cmpBaseColors.bluishBackground;
}

type EllipsisProps = {
  tokens: string[];
  setIsTruncated: (t: boolean) => void;
  startWithDot?: boolean;
  resizeSignal: ResizeObserverEntry[];
};

const Ellipsis = ({ tokens, setIsTruncated, startWithDot = false, resizeSignal }: EllipsisProps) => {
  const boxEl = useRef<HTMLDivElement>(null);
  const label = tokens.join(".");

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      if (!boxEl.current) {
        return;
      }
      if (boxEl.current.offsetWidth < boxEl.current.scrollWidth) {
        const text = boxEl.current.textContent;
        boxEl.current.dataset.tail = text?.slice(text?.length - (tokens[tokens.length - 1]?.length ?? 0)) ?? "";
        setIsTruncated(true);
      } else {
        boxEl.current.dataset.tail = "";
        setIsTruncated(false);
      }
    });
  }, [label, tokens, resizeSignal, setIsTruncated]);

  return (
    <Box
      ref={boxEl}
      sx={{
        ...ellipsisSx,
        "&:before": {
          maxWidth: "75%",
          float: "right",
          content: "attr(data-tail)",
          ...ellipsisSx,
        },
      }}
    >
      {startWithDot ? `.` : ""}
      {label}
    </Box>
  );
};

type LabelSlot = (setIsTruncated: Dispatch<boolean>, resizeSignal: ResizeObserverEntry[]) => ReactNode;

interface FieldChipProps {
  labelSlot: LabelSlot;
  onDelete?: () => void;
  onClick?: () => void;
  selected?: boolean;
  isVariableReference?: boolean;
  variableValue?: string;
  hideTooltip?: boolean;
}

export const FieldChip: FC<FieldChipProps> = ({
  labelSlot,
  onDelete,
  onClick,
  selected = false,
  isVariableReference,
  variableValue,
  hideTooltip = false,
}) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const resizeSignal = useResizeObserverSignal(chipRef.current);
  const [isTruncated, setIsTruncated] = useState(false);

  const label = labelSlot((isTruncated) => {
    setIsTruncated((prev) => prev || isTruncated);
  }, resizeSignal);

  if (!label) {
    return null;
  }

  const chipLabel =
    isVariableReference && variableValue ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {label}
        <InfoOutlinedIcon sx={{ fontSize: "16px", color: "primary.main" }} />
      </Box>
    ) : (
      label
    );

  let tooltipContent = "";

  if (!hideTooltip) {
    if (isVariableReference && variableValue) {
      tooltipContent = `Value: ${variableValue}`;
    } else if (isTruncated) {
      tooltipContent = chipRef.current?.innerText.replaceAll("\n", "") || "";
    }
  }

  return (
    <Tooltip title={tooltipContent} placement="bottom" arrow>
      <Chip
        ref={chipRef}
        label={chipLabel}
        size="small"
        sx={[
          ({ palette }) => ({
            borderRadius: 1,
            borderColor: isVariableReference ? colors.dark.primary.light : getBorderColor(palette, selected),
            borderStyle: "solid",
            borderWidth: 1,
            backgroundColor: isVariableReference
              ? colors.dark.primary.hoverBackground
              : colors.light.primary.hoverBackground,
            color: "primary.main",
            "&.Mui-focusVisible": {
              backgroundColor: isVariableReference
                ? colors.dark.primary.hoverBackground
                : colors.light.primary.hoverBackground,
            },
            "& .MuiChip-label": {
              display: "flex",
              flexWrap: "nowrap",
            },
          }),
        ]}
        onDelete={onDelete}
        deleteIcon={<CloseIcon sx={({ palette }) => ({ fill: palette.action.active })} />}
        onClick={onClick}
      />
    </Tooltip>
  );
};

type ReadonlyReferencedFieldChipProps = {
  referenceableNodes: NodeWitOutputModel[];
  value: ReferencedNodeValue;
  onDelete?: () => void;
  hideTooltip?: boolean;
};

export const ReadonlyReferencedFieldChip = ({
  referenceableNodes,
  value,
  onDelete,
  hideTooltip,
}: ReadonlyReferencedFieldChipProps) => {
  const referencedNode = referenceableNodes.find(({ id }) => id === value.referencedNodeId);
  const { isVariableRef, variableValue, variableMaps } = useVariableReference(referencedNode, value);

  if (!referencedNode) {
    return null;
  }

  const defaultTokens = [getReferencedNodeLabel(referencedNode), ...value.referencedField];
  const tokens = processTriggerNodeTokens(defaultTokens, referencedNode, variableMaps);

  const labelSlot: LabelSlot = (setIsTruncated, resizeSignal) => (
    <Ellipsis tokens={tokens} setIsTruncated={setIsTruncated} resizeSignal={resizeSignal} />
  );

  return (
    <FieldChip
      labelSlot={labelSlot}
      onDelete={onDelete}
      isVariableReference={isVariableRef}
      variableValue={variableValue}
      hideTooltip={hideTooltip}
    />
  );
};

type ReferencedFieldChipProps = ReadonlyReferencedFieldChipProps & {
  onChange: (value: ReferencedNodeValue) => void;
  onClick?: () => void;
  selected?: boolean;
};

export const ReferencedFieldChip = ({
  referenceableNodes,
  value,
  onDelete,
  onChange,
  onClick,
  selected = false,
}: ReferencedFieldChipProps) => {
  const referencedNode = referenceableNodes.find(({ id }) => id === value.referencedNodeId);
  const { isVariableRef, variableValue, variableMaps } = useVariableReference(referencedNode, value);

  if (!referencedNode) {
    return null;
  }

  const augmentedTokens = [
    { member: getReferencedNodeLabel(referencedNode), memberModel: referencedNode.outputModel },
    ...augmentDataPathWithModelTypes(referencedNode.outputModel, value.referencedField).filter(
      ({ member }) => member !== ""
    ),
  ];

  const { labelItems } = augmentedTokens.reduce(
    (acc, token, index, allTokens) => {
      const key = allTokens
        .map(({ member }) => member)
        .slice(0, index + 1)
        .join(".");
      if (allTokens[index - 1]?.memberModel.type === ModelType.MAP) {
        acc.labelItems.push({
          type: "customToken",
          key,
          token,
          onChange: (pathToken) => {
            const newReferencedField = Array.from(value.referencedField);
            newReferencedField.splice(index - 1, 1, pathToken);
            onChange({
              referencedNodeId: value.referencedNodeId,
              referencedField: newReferencedField,
            });
          },
        });
        acc.lastMapIndex += 1;
        return acc;
      }
      if (token.memberModel.type === ModelType.MAP || index === allTokens.length - 1) {
        const tokens = allTokens.slice(acc.lastMapIndex + 1, index + 1).map((token) => token.member);
        acc.labelItems.push({
          type: "ellipsis",
          key,
          tokens,
          startWithDot: acc.lastMapIndex !== -1,
        });
        acc.lastMapIndex = index;
        return acc;
      }
      return acc;
    },
    {
      labelItems: [] as (
        | ({ type: "ellipsis"; key: string } & Omit<EllipsisProps, "setIsTruncated" | "resizeSignal">)
        | ({ type: "customToken"; key: string } & ReferencedFieldChipCustomTokenProps)
      )[],
      lastMapIndex: -1,
    }
  );

  if (labelItems.length === 0) {
    return null;
  }

  if (isTriggerNode(referencedNode as unknown as NodeModelWithId)) {
    const firstItem = labelItems[0];
    if (firstItem?.type === "ellipsis" && firstItem.tokens[1] === "variables") {
      firstItem.tokens = processTriggerNodeTokens(firstItem.tokens, referencedNode, variableMaps);
    }
  }

  return (
    <FieldChip
      labelSlot={(setIsTruncated, resizeSignal) =>
        labelItems.map((item) =>
          item.type === "ellipsis" ? (
            <Ellipsis
              key={item.key}
              tokens={item.tokens}
              setIsTruncated={setIsTruncated}
              resizeSignal={resizeSignal}
              startWithDot={item.startWithDot}
            />
          ) : (
            <ReferencedFieldChipCustomToken key={item.key} token={item.token} onChange={item.onChange} />
          )
        )
      }
      onDelete={onDelete}
      onClick={onClick}
      selected={selected}
      isVariableReference={isVariableRef}
      variableValue={variableValue}
    />
  );
};
